import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getProjects, getProjectCategories, toggleProjectFavorite, updateProjectStatus, deleteProject } from '../../../utils/api';
import NewProjectModal from './NewProjectModal';
import Table from '../../common/Table';

const ProjectsView = ({ onViewProject }) => {
    const jwtToken = useSelector((state) => state.auth.jwtToken);
    const [activeFilter, setActiveFilter] = useState('active');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [categories, setCategories] = useState([]);
    const [isLoadingCategories, setIsLoadingCategories] = useState(false);
    const [projects, setProjects] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [isNewProjectModalOpen, setIsNewProjectModalOpen] = useState(false);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [openUsersPopupId, setOpenUsersPopupId] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 100,
        total: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false,
    });

    const filterTabs = [
        { id: 'active', label: 'Active' },
        { id: 'completed', label: 'Completed' },
        { id: 'favourite', label: 'Favourite' },
        { id: 'all', label: 'All' }
    ];

    // Fetch projects from API
    const fetchProjects = async (pageNumber = 1, isLoadMore = false) => {
        if (!jwtToken) return;
        
        if (isLoadMore) {
            setIsLoadingMore(true);
        } else {
            setIsLoading(true);
        }
        setError(null);
        
        try {
            const response = await getProjects({
                search: searchQuery,
                filter: activeFilter,
                category: selectedCategory,
                limit: pagination.limit,
                page: pageNumber,
            });
            
            console.log('Projects API Response:', response);
            
            // Handle response - API structure: { projects: [], pagination: {} }
            if (response && response.data) {
                const fetchedProjects = response.data.projects || [];
                
                // Update projects - replace on first page, append on load more
                setProjects(prevProjects => {
                    if (pageNumber === 1) {
                        return fetchedProjects;
                    }
                    return [...prevProjects, ...fetchedProjects];
                });
                
                // Update pagination - map API pagination structure
                if (response.data.pagination) {
                    const apiPagination = response.data.pagination;
                    setPagination(prev => ({
                        ...prev,
                        page: apiPagination.currentPage || pageNumber,
                        limit: apiPagination.pageSize || prev.limit,
                        total: apiPagination.totalCount || 0,
                        total_pages: apiPagination.totalPages || 1,
                        has_next: (apiPagination.currentPage || pageNumber) < (apiPagination.totalPages || 1),
                        has_prev: (apiPagination.currentPage || pageNumber) > 1
                    }));
                } else {
                    // Fallback pagination logic
                    const hasMore = fetchedProjects.length === pagination.limit;
                    setPagination(prev => ({
                        ...prev,
                        page: pageNumber,
                        has_next: hasMore,
                        has_prev: pageNumber > 1
                    }));
                }
            } else {
                // No data
                if (pageNumber === 1) {
                    setProjects([]);
                }
                setPagination(prev => ({ ...prev, has_next: false }));
            }
        } catch (err) {
            console.error('Failed to fetch projects:', err);
            setError(err.message || 'Failed to load projects');
            if (pageNumber === 1) {
                setProjects([]);
            }
        } finally {
            if (isLoadMore) {
                setIsLoadingMore(false);
            } else {
                setIsLoading(false);
            }
        }
    };

    // Fetch categories on mount
    useEffect(() => {
        const fetchCategories = async () => {
            if (!jwtToken) return;
            
            setIsLoadingCategories(true);
            try {
                const response = await getProjectCategories();
                console.log('Categories API Response:', response);
                
                if (response && response.data) {
                    const fetchedCategories = response.data.categories || response.data.data || response.data || [];
                    setCategories(fetchedCategories);
                }
            } catch (err) {
                console.error('Failed to fetch categories:', err);
            } finally {
                setIsLoadingCategories(false);
            }
        };
        
        fetchCategories();
    }, [jwtToken]);

    // Fetch projects on mount and when filter/search/category changes
    useEffect(() => {
        if (jwtToken) {
            fetchProjects(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilter, selectedCategory, jwtToken]);

    // Debounce search
    useEffect(() => {
        if (jwtToken) {
            const debounceTimer = setTimeout(() => {
                fetchProjects(1);
            }, 500);
            
            return () => clearTimeout(debounceTimer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery]);

    // Handle load more
    const handleLoadMore = () => {
        if (pagination.has_next && !isLoadingMore && !isLoading) {
            const nextPage = pagination.page + 1;
            fetchProjects(nextPage, true); // isLoadMore = true
        }
    };

    // Handle toggle favorite
    const handleToggleFavorite = async (projectId, currentFavouriteStatus) => {
        if (!jwtToken) return;
        
        const newFavouriteStatus = !currentFavouriteStatus;
        try {
            const response = await toggleProjectFavorite(projectId, newFavouriteStatus);

            if (response && response.data) {
                setProjects(prevProjects => 
                    prevProjects.map(project => {
                        if (project.id === projectId || project.project_id === projectId) {
                            return {
                                ...project,
                                favourite: newFavouriteStatus
                            };
                        }
                        return project;
                    })
                );
            }else {
                alert('Failed to update favorite status. Please try again.');
            }
        } catch (err) {
            console.error('Failed to toggle favorite:', err);
            alert('Failed to update favorite status. Please try again.');
        }
    };

    // Handle set project status
    const handleSetProjectStatus = async (projectId, newStatus) => {
        if (!jwtToken) return;
        
        try {
            const response = await updateProjectStatus(projectId, newStatus);

            if (response && response.data) {
                setProjects(prevProjects => 
                    prevProjects.map(project => {
                        if (project.id === projectId || project.project_id === projectId) {
                            
                            return {
                                ...project,
                                status: parseInt(newStatus)
                            };
                        }
                        return project;
                    })
                );
            } else {
                alert('Failed to update project status. Please try again.');
            }
        } catch (err) {
            console.error('Failed to update project status:', err);
            alert('Failed to update project status. Please try again.');
        }
    };

    // Handle delete project
    const handleDeleteProject = async (projectId) => {
        if (!jwtToken) return;

        const confirmed = window.confirm('Are you sure you want to delete this project? This action cannot be undone. and all the data will be deleted permanently.');
        if (!confirmed) return;

        try {
            const response = await deleteProject(projectId);
            if (response && response.data) {
                fetchProjects(1);
            } else {
                alert('Failed to delete project. Please try again.');
            }
        }
        catch (err) {
            console.error('Failed to delete project:', err);
            alert('Failed to delete project. Please try again.');
        }
    };

    // Handle project created callback
    const handleProjectCreated = () => {
        // Refresh projects list
        fetchProjects(1);
    };

    // Close menu/popup when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openMenuId && !event.target.closest('.octopus-table-menu-wrapper')) {
                setOpenMenuId(null);
            }
            if (openUsersPopupId && !event.target.closest('.octopus-table-users-popup-wrapper')) {
                setOpenUsersPopupId(null);
            }
        };

        if (openMenuId || openUsersPopupId) {
            document.addEventListener('click', handleClickOutside);
        }

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [openMenuId, openUsersPopupId]);

    // Format date helper
    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        } catch (e) {
            return '';
        }
    };

    // Define table columns with render functions
    const projectIconColors = ['#9333ea', '#ec4899', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];
    const tableColumns = [
        {
            key: 'name',
            label: 'Name',
            headerStyle: { width: '35%', minWidth: '250px' },
            render: (row, rowIndex) => {
                const rowId = row.id || row.project_id || rowIndex;
                const iconColor = projectIconColors[rowId % projectIconColors.length];
                return (
                    <div className="octopus-table-name-cell">
                        <div 
                            className="octopus-table-icon"
                            style={{ backgroundColor: iconColor }}
                        >
                            <i className="fa-solid fa-list"></i>
                        </div>
                        <div className="octopus-table-name-content">
                            <span className="octopus-table-name-text">
                                {row.title || row.name}
                            </span>
                        </div>
                    </div>
                );
            }
        },
        {
            key: 'members',
            label: 'Members',
            headerStyle: { width: '20%', minWidth: '150px' },
            render: (row, rowIndex) => {
                const projectUsers = row.users || [];
                const rowId = row.id || row.project_id || rowIndex;
                return (
                    <div className="octopus-table-members-cell">
                        {projectUsers.length > 0 ? (
                            <div className="octopus-table-members-avatars">
                                {projectUsers.slice(0, 5).map((user, index) => (
                                    <div 
                                        key={user.ID || user.id || index}
                                        className="octopus-table-member-avatar"
                                        title={user.display_name || user.name || 'User'}
                                        style={{ zIndex: projectUsers.length - index }}
                                    >
                                        {user.image ? (
                                            <img 
                                                src={user.image} 
                                                alt={user.display_name || user.name || 'User'}
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.nextSibling.style.display = 'flex';
                                                }}
                                            />
                                        ) : null}
                                        <div 
                                            className="octopus-table-member-avatar-placeholder"
                                            style={{ display: user.image ? 'none' : 'flex' }}
                                        >
                                            {(user.display_name || user.name || 'U').charAt(0).toUpperCase()}
                                        </div>
                                    </div>
                                ))}
                                {projectUsers.length > 5 && (
                                    <div className="octopus-table-users-popup-wrapper">
                                        <div 
                                            className="octopus-table-member-avatar-more"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setOpenUsersPopupId(openUsersPopupId === rowId ? null : rowId);
                                            }}
                                            title={`${projectUsers.length - 5} more members`}
                                        >
                                            +{projectUsers.length - 5}
                                        </div>
                                        {openUsersPopupId === rowId && (
                                            <div className="octopus-table-users-popup">
                                                {projectUsers.slice(5).map((user, index) => (
                                                    <div 
                                                        key={user.ID || user.id || index}
                                                        className="octopus-table-popup-avatar"
                                                        title={user.display_name || user.name || 'User'}
                                                    >
                                                        {user.image ? (
                                                            <img 
                                                                src={user.image} 
                                                                alt={user.display_name || user.name || 'User'}
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    e.target.nextSibling.style.display = 'flex';
                                                                }}
                                                            />
                                                        ) : null}
                                                        <div 
                                                            className="octopus-table-popup-avatar-placeholder"
                                                            style={{ display: user.image ? 'none' : 'flex' }}
                                                        >
                                                            {(user.display_name || user.name || 'U').charAt(0).toUpperCase()}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <span className="octopus-table-no-members">-</span>
                        )}
                    </div>
                );
            }
        },
        {
            key: 'categories',
            label: 'Categories',
            headerStyle: { width: '25%', minWidth: '200px' },
            render: (row) => {
                const projectCategories = row.categories || [];
                return projectCategories.length > 0 ? (
                    <div className="octopus-table-categories-cell">
                        {projectCategories.map((category, index) => (
                            <span 
                                key={category.id || category.category_id || index}
                                className="octopus-table-category-badge"
                            >
                                {category.name || category.title || 'Category'}
                            </span>
                        ))}
                    </div>
                ) : (
                    <span className="octopus-table-no-category">-</span>
                );
            }
        },
        {
            key: 'lastModified',
            label: 'Last modified',
            sortable: false,
            headerStyle: { width: '15%', minWidth: '120px' },
            render: (row) => (
                <span className="octopus-table-date">
                    {formatDate(row.lastModified) || '-'}
                </span>
            )
        },
        {
            key: 'actions',
            label: '',
            headerStyle: { width: '5%', minWidth: '80px', textAlign: 'right' },
            render: (row, rowIndex) => {
                const projectId = row.id || row.project_id || rowIndex;
                const isFavourite = row.favourite || (row.favourite && row.favourite !== '0' && row.favourite !== '');
                const isMenuOpen = openMenuId === projectId;
                return (
                    <div className="octopus-table-row-actions">
                        <button 
                            className={`octopus-table-row-favourite ${isFavourite ? 'active' : ''}`}
                            title={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleToggleFavorite(projectId, isFavourite);
                            }}
                        >
                            <i className="fa-solid fa-star"></i>
                        </button>
                        <div className="octopus-table-menu-wrapper">
                            <button 
                                className="octopus-table-row-menu"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenuId(isMenuOpen ? null : projectId);
                                }}
                            >
                                <i className="fa-solid fa-ellipsis-v"></i>
                            </button>
                            {isMenuOpen && (
                                <div className="octopus-table-row-dropdown">
                                    <button 
                                        className="octopus-table-dropdown-item"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const newStatus = row.status === 1 ? 0 : 1;
                                            handleSetProjectStatus(projectId, newStatus);
                                            setOpenMenuId(null);
                                        }}
                                    >
                                        {row.status === 1 ? (   
                                            <>
                                                <i className="fa-solid fa-rotate-left"></i>
                                                Restore
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa-solid fa-circle-check"></i>
                                                Complete
                                            </>
                                        )}
                                    </button>
                                    <button 
                                        className="octopus-table-dropdown-item"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteProject(projectId);
                                            setOpenMenuId(null);
                                        }}
                                    >
                                        <i className="fa-solid fa-trash"></i>
                                        Delete
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                );
            }
        }
    ];

    // Filter projects based on status and prepare data
    const filteredProjects = projects
        .filter((project) => {
            if (project.status === 1 && activeFilter === 'active') {
                return false;
            }
            if (project.status === 0 && activeFilter === 'completed') {
                return false;
            }
            return true;
        })
        .map(project => ({
            ...project,
            lastModified: project.updated_at || project.created_at || ''
        }));

    return (
        <div className="octopus-projects-manager">
            {/* <div className="octopus-projects-manager-header">
                <h2 className="octopus-projects-manager-title">Project Manager</h2>
                <div className="octopus-projects-manager-actions">
                    <button 
                        className="octopus-projects-btn octopus-projects-btn-primary"
                        onClick={() => setIsNewProjectModalOpen(true)}
                    >
                        <span className="octopus-icon-plus">+</span>
                        New Project
                    </button>
                    <div className="octopus-projects-search">
                        <input
                            type="text"
                            placeholder="Search ..."
                            className="octopus-projects-search-input"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
            </div> */}

            {/* Filter Tabs */}
            <div className="octopus-projects-filters">
                <div className="octopus-projects-filter-tabs">
                    {filterTabs.map((tab) => (
                        <button
                            key={tab.id}
                            className={`octopus-projects-filter-tab ${activeFilter === tab.id ? 'active' : ''} ${tab.id}-filter`}
                            onClick={() => setActiveFilter(tab.id)}
                        >
                            {tab.id === 'active' && (
                                <i className="fa-solid fa-list-check"></i>
                            )}
                            {tab.id === 'completed' && (
                                <i className="fa-solid fa-circle-check"></i>
                            )}
                            {tab.id === 'favourite' && (
                                <i className="fa-solid fa-star"></i>
                            )}
                            {tab.id === 'all' && (
                                <i className="fa-solid fa-th"></i>
                            )}
                            {tab.label}
                        </button>
                    ))}
                </div>
                

                <div className="octopus-projects-view-controls">
                    <button 
                        className="octopus-projects-btn octopus-projects-btn-primary"
                        onClick={() => setIsNewProjectModalOpen(true)}
                    >
                        <span className="octopus-icon-plus">+</span>
                        New Project
                    </button>
                    <div className="octopus-projects-search">
                        <input
                            type="text"
                            placeholder="Search ..."
                            className="octopus-projects-search-input"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <select 
                        className="octopus-projects-category-select"
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        disabled={isLoadingCategories}
                    >
                        <option value="">- All Categories -</option>
                        {categories.map((category) => (
                            <option key={category.id || category.category_id} value={category.id || category.category_id}>
                                {category.name || category.title}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Projects Table */}
            <div className="octopus-projects-content">
                {isLoading && projects.length === 0 ? (
                    <div className="octopus-projects-empty">
                        <p>Loading projects...</p>
                    </div>
                ) : error ? (
                    <div className="octopus-projects-empty">
                        <p>Error: {error}</p>
                    </div>
                ) : projects.length === 0 ? (
                    <div className="octopus-projects-empty">
                        <p>No projects found. Create a new project to get started!</p>
                    </div>
                ) : (
                    <Table
                        columns={tableColumns}
                        data={filteredProjects}
                        onRowClick={(projectId) => onViewProject && onViewProject(projectId)}
                    />
                )}
                
                {/* Load More Button */}
                {projects.length > 0 && pagination.has_next && (
                    <div className="octopus-projects-load-more">
                        <button
                            className="octopus-projects-load-more-btn"
                            onClick={handleLoadMore}
                            disabled={isLoadingMore || isLoading}
                        >
                            {isLoadingMore ? (
                                <>
                                    <i className="fa-solid fa-spinner fa-spin"></i>
                                    Loading...
                                </>
                            ) : (
                                <>
                                    Load More
                                    <i className="fa-solid fa-arrow-down"></i>
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>

            {/* New Project Modal */}
            <NewProjectModal
                isOpen={isNewProjectModalOpen}
                onClose={() => setIsNewProjectModalOpen(false)}
                categories={categories}
                onProjectCreated={handleProjectCreated}
            />
        </div>
    );
};

export default ProjectsView;

